import { Logger, LoggerLevel } from '../Logger';
import { isoFormat } from '../../shared/util/DateUtils';

const LOGGER_PREFIX = 'com.firstlight.vstb7';

/**
 * Creates the logger for logging the error, warning , other information.
 * By default the logger level are set to debug
 *
 * @constructor LoggerAdapter
 * @param logLevel optional [{@link LoggerLevel}]
 * Accepts log level as input. Based on the level, logs will be filtered
 *
 * @param tag  optional Accepts the tag name as input. It can be class name or module name or method name .
 */
export class LoggerAdapter implements Logger {
  private logLevel_: LoggerLevel;
  private tag_: string;

  public get logLevel(): LoggerLevel {
    return this.logLevel_;
  }

  constructor(logLevel: LoggerLevel = LoggerLevel.DEBUG, tag = '') {
    this.logLevel_ = logLevel;
    this.tag_ = tag;
  }

  public error(message: string, ...optionalParams: any[]): void {
    this.doLog_('error', LoggerLevel.ERROR, message, ...optionalParams);
  }

  public warn(message: string, ...optionalParams: any[]): void {
    this.doLog_('warn', LoggerLevel.WARN, message, ...optionalParams);
  }

  public info(message: string, ...optionalParams: any[]): void {
    this.doLog_('info', LoggerLevel.INFO, message, ...optionalParams);
  }

  public debug(message: string, ...optionalParams: any[]): void {
    this.doLog_('debug', LoggerLevel.DEBUG, message, ...optionalParams);
  }

  public log(message: string, ...optionalParams: any[]): void {
    this.doLog_('log', LoggerLevel.VERBOSE, message, ...optionalParams);
  }

  public time(timerName: string): void {
    this.calculateMetrics_('time', timerName);
  }

  public timeEnd(timerName: string): void {
    this.calculateMetrics_('timeEnd', timerName);
  }

  public count(countTitle?: string): void {
    this.calculateMetrics_('count', countTitle);
  }

  public countReset(countTitle?: string): void {
    this.calculateMetrics_('countReset', countTitle);
  }

  private doLog_ = (
    messageType: 'debug' | 'info' | 'warn' | 'error' | 'log',
    level: LoggerLevel,
    message: string,
    ...optionalParams: any[]
  ): void => {
    if (this.logLevel >= level) {
      const messageToWrite = `${isoFormat(new Date())} ${this.tag_} ${message}`;
      console[messageType](messageToWrite, ...optionalParams);
    }
  };

  private calculateMetrics_ = (messageType: 'time' | 'timeEnd' | 'count' | 'countReset', message = ''): void => {
    if (this.logLevel >= LoggerLevel.DEBUG) {
      console[messageType](`${LOGGER_PREFIX} ${message}`);
    } else {
      this.log(`${messageType} only available when logLevel === LoggerLevel.DEBUG`, message);
    }
  };
}
